import { Box, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeepCompareMemo } from 'use-deep-compare';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { StrictDateRange } from '../../commonUI/DateRangeFilterSelect/types';
import { generateDateRangeFilter } from '../../commonUI/DateRangeFilterSelect/utils/generateDateRangeFilter';
import { useCollectionsFiltered } from '../hooks/useCollectionsFiltered';
import {
  CollectionsDateRangeFilter,
  DEFAULT_COLLECTIONS_RANGE_FILTER_VALUE,
} from './CollectionsDateRangeFilter';
import { CollectionsTable } from './CollectionsTable';

type Props = {
  ranchIds?: string[];
  loading?: boolean;
};

/**
 * Displays the Collections for each of the ranches (via ranchIds)
 */
export const CollectionsExplorer = ({ ranchIds, loading }: Props) => {
  /**
   * Collections Date Range Filter Logic
   */
  const [dateRangeFilter, setDateRangeFilter] = useState<
    StrictDateRange | undefined
  >(
    generateDateRangeFilter(
      DEFAULT_COLLECTIONS_RANGE_FILTER_VALUE
    ) as StrictDateRange
  );

  const handleDateRangeFilterChange = useCallback(
    (newRangeFilter?: StrictDateRange) => {
      setDateRangeFilter(newRangeFilter);
    },
    []
  );

  /**
   * Get Collections for the selected ranches and within the specified
   * date range
   */
  const {
    currentData: collections,
    isLoading,
    isFetching,
    isUninitialized,
  } = useCollectionsFiltered(ranchIds, dateRangeFilter);
  const isLoadingCollections = isLoading || isFetching;

  /**
   * Collection Table Row Selection Logic
   */
  const navigate = useNavigate();

  const sortedCollections = useDeepCompareMemo(() => {
    return collections
      ? [...collections].sort((a, b) => {
          return (
            new Date(b.collectionDate).getTime() -
            new Date(a.collectionDate).getTime()
          );
        })
      : undefined;
  }, [collections]);

  return (
    <Stack direction='column' spacing={4} sx={{ padding: 6, flexGrow: 1 }}>
      <Stack
        direction='row'
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant='h5'>Collections</Typography>
        <CollectionsDateRangeFilter onChange={handleDateRangeFilterChange} />
      </Stack>
      {(!sortedCollections || sortedCollections.length === 0) &&
      !isLoadingCollections &&
      !isUninitialized &&
      !loading ? (
        <NoCollections />
      ) : (
        <CollectionsTable
          collections={sortedCollections}
          onRowClick={(row) => navigate(`/collections/${row.id}`)}
          loading={loading || isLoadingCollections || isUninitialized}
        />
      )}
    </Stack>
  );
};

const NoCollections = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Typography fontSize={24}>{t('No Collections found')}</Typography>
    </Box>
  );
};
